/**
 * EventClickResolver.js - Resolves event clicks to event data and triggers modal display
 *
 * Responsibilities:
 * - Handle list view event click processing
 * - Resolve event data from multiple sources (calendarApp, global handlers, DOM)
 * - Find events by title with fuzzy matching
 * - Extract date information from DOM elements
 * - Delegate to modal display once event is resolved
 *
 * This class is intentionally decoupled from ModalManager to maintain
 * single responsibility - ModalManager handles UI, this handles data resolution.
 */

import ErrorHandler from '../../utils/ErrorHandler.js';

export default class EventClickResolver {
  /**
   * @param {Object} options
   * @param {Function} options.showModal - Callback to show modal with content (receives HTML string)
   * @param {Function} options.showEventModal - Callback to show event modal (receives event object, dateText)
   * @param {boolean} options.debug - Enable debug logging
   * @param {Object} options.calendarApp - CalendarApplication instance (injected dependency)
   * @param {Object} options.eventDataManager - Event data manager instance (injected dependency)
   * @param {Object} options.calendarRenderer - Calendar renderer instance (injected dependency)
   * @param {Object} options.modalAdapter - Modal adapter instance (injected dependency)
   */
  constructor(options = {}) {
    this.showModal = options.showModal || (() => false);
    this.showEventModal = options.showEventModal || null;

    // Injected dependencies (no global fallbacks needed)
    this._calendarApp = options.calendarApp || null;
    this._eventDataManager = options.eventDataManager || null;
    this._calendarRenderer = options.calendarRenderer || null;
    this._modalAdapter = options.modalAdapter || null;

    this.errorHandler = new ErrorHandler({
      module: 'EventClickResolver',
      maxErrors: 20,
      debug: options.debug || false
    });
  }

  /**
   * Handle event click from list view
   * @param {Event} e - Click event
   * @param {Element} titleDiv - The clicked title element
   * @returns {Promise<boolean>} Success status
   */
  async handleEventClick(e, titleDiv) {
    try {
      e.preventDefault();
      e.stopPropagation();

      const titleText = titleDiv.textContent.trim();
      const parentItem = titleDiv.closest('.event-item');

      this.errorHandler.logInfo('List view event clicked', {
        category: 'click-processing',
        action: 'event-clicked',
        titleText
      });

      if (!parentItem) {
        this.errorHandler.handleError('No parent event item found for click', null, {
          category: 'click-processing',
          action: 'missing-parent-item',
          titleText
        });
        return false;
      }

      // Try to handle the click using the new modular system
      const newSystemResult = await this._handleViaModularSystem(titleText, parentItem);
      if (newSystemResult) {
        return true;
      }

      // Fallback: Look for embedded modal content in the DOM
      const legacyResult = this._handleViaLegacyDOM(parentItem);
      if (legacyResult) {
        return true;
      }

      this.errorHandler.handleError('All event resolution strategies failed', null, {
        category: 'click-processing',
        action: 'all-strategies-failed',
        titleText
      });

      return false;
    } catch (error) {
      this.errorHandler.handleFatalError('Critical error handling event click', error, {
        category: 'click-processing',
        action: 'event-click-fatal-error'
      });
      return false;
    }
  }

  /**
   * Try to handle event click using the modular calendar system
   */
  async _handleViaModularSystem(titleText, parentItem) {
    try {
      // Method 1: Try using injected dependencies (preferred)
      if (this._calendarApp && this._calendarApp.state) {
        const eventData = this._calendarApp.state.components.get('eventData');
        const modalAdapter = this._modalAdapter || this._calendarApp.state.components.get('modal');

        if (eventData && modalAdapter) {
          this.errorHandler.logInfo('Using modular system for list view click', {
            category: 'click-processing',
            action: 'modular-system-attempt'
          });

          const allEvents = this._getEventsFromSources(eventData);

          if (allEvents.length === 0) {
            this.errorHandler.handleError('No events found in any data source', null, {
              category: 'fallback',
              action: 'no-events-available'
            });
            return false;
          }

          const event = this._findEventByTitle(allEvents, titleText);

          if (event) {
            const dateText = this._extractDateText(parentItem);
            const success = await modalAdapter.showEventModal(event, dateText);

            if (success) {
              this.errorHandler.logSuccess('Event modal shown via modular system', {
                category: 'modal-operation',
                action: 'modular-system-success',
                eventTitle: titleText
              });
            } else {
              this.errorHandler.handleError('Modal adapter failed to show event', null, {
                category: 'modal-operation',
                action: 'modal-adapter-failed',
                eventTitle: titleText
              });
            }

            return success;
          } else {
            this.errorHandler.handleError('Event not found in modular system', null, {
              category: 'fallback',
              action: 'event-not-found',
              titleText,
              availableEvents: allEvents.length
            });
          }
        }
      }

      // Method 2: Try using showEventModal callback if provided
      if (this.showEventModal) {
        const allEvents = this._getEventsFromAllSources();

        if (allEvents.length > 0) {
          const event = this._findEventByTitle(allEvents, titleText);

          if (event) {
            const dateText = this._extractDateText(parentItem);
            const success = await this.showEventModal(event, dateText);

            if (success) {
              this.errorHandler.logSuccess('Event shown via showEventModal callback', {
                category: 'modal-operation',
                action: 'callback-success',
                eventTitle: titleText
              });
            }

            return success;
          }
        }
      }

      return false;
    } catch (error) {
      this.errorHandler.handleError('Error in modular system event click handling', error, {
        category: 'fallback',
        action: 'modular-system-error'
      });
      return false;
    }
  }

  /**
   * Try to handle event click using legacy DOM-embedded content
   */
  _handleViaLegacyDOM(parentItem) {
    try {
      let modalContent = parentItem.querySelector('.event-modal-content');
      if (!modalContent) {
        modalContent = parentItem.querySelector('.event-description-content');
      }

      if (modalContent) {
        const success = this.showModal(modalContent.innerHTML);

        if (success) {
          this.errorHandler.logSuccess('Event shown via legacy DOM content', {
            category: 'fallback',
            action: 'legacy-dom-success'
          });
        }

        return success;
      }

      return false;
    } catch (error) {
      this.errorHandler.handleError('Error in legacy DOM handling', error, {
        category: 'fallback',
        action: 'legacy-dom-error'
      });
      return false;
    }
  }

  /**
   * Get events from eventData component and fallback sources
   */
  _getEventsFromSources(eventData) {
    let allEvents = [];

    try {
      if (eventData.getAllEvents) {
        allEvents = eventData.getAllEvents();
      }

      if (allEvents.length === 0 && typeof eventData.parseEventsFromDOM === 'function') {
        eventData.parseEventsFromDOM();
        allEvents = eventData.getAllEvents();
      }

      if (allEvents.length === 0) {
        allEvents = this._getEventsFromAllSources();
      }
    } catch (error) {
      this.errorHandler.handleError('Error getting events from sources', error, {
        category: 'fallback',
        action: 'event-sources-error'
      });
    }

    return allEvents;
  }

  /**
   * Get events from all available sources (injected dependencies preferred)
   */
  _getEventsFromAllSources() {
    const sources = [
      { name: 'eventDataManager', fn: () => this._eventDataManager?.transformedEvents },
      { name: 'calendarRenderer', fn: () => this._calendarRenderer?.events },
      { name: 'calendarApp', fn: () => this._calendarApp?.state?.components?.get('eventData')?.getAllEvents?.() },
      { name: 'calendarInstance', fn: () => this._getEventsFromCalendarInstance() }
    ];

    for (const source of sources) {
      try {
        const events = source.fn();
        if (events && events.length > 0) {
          this.errorHandler.logInfo(`Events loaded from ${source.name}`, {
            category: 'fallback',
            action: 'source-found',
            source: source.name,
            count: events.length
          });
          return events;
        }
      } catch (e) {
        this.errorHandler.logInfo(`Source ${source.name} unavailable`, {
          category: 'fallback',
          action: 'source-unavailable',
          source: source.name
        });
      }
    }

    return [];
  }

  /**
   * Get events from FullCalendar instance
   */
  _getEventsFromCalendarInstance() {
    try {
      const calendar = this._findCalendarInstance();
      if (calendar && calendar.getEvents) {
        const calendarEvents = calendar.getEvents();
        return calendarEvents.map(event => ({
          id: event.id,
          title: event.title,
          start: event.start,
          end: event.end,
          allDay: event.allDay,
          description: event.extendedProps?.description || '',
          location: event.extendedProps?.location || '',
          extendedProps: event.extendedProps
        }));
      }
    } catch (error) {
      this.errorHandler.handleError('Error getting events from calendar instance', error, {
        category: 'fallback',
        action: 'calendar-instance-error'
      });
    }

    return [];
  }

  /**
   * Find event by title with exact and fuzzy matching
   */
  _findEventByTitle(allEvents, titleText) {
    // Try exact title match first
    let event = allEvents.find(e => (e.title || e.summary) === titleText);

    // If exact match fails, try fuzzy matching
    if (!event) {
      event = allEvents.find(e => {
        const eventTitle = e.title || e.summary || '';
        return eventTitle.toLowerCase().includes(titleText.toLowerCase()) ||
               titleText.toLowerCase().includes(eventTitle.toLowerCase());
      });
    }

    return event;
  }

  /**
   * Extract formatted date text from parent item's data attribute
   */
  _extractDateText(parentItem) {
    const rawIso = parentItem.dataset.eventDate;
    if (rawIso) {
      try {
        return new Date(rawIso).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      } catch (e) {
        return rawIso;
      }
    }
    return '';
  }

  /**
   * Find FullCalendar instance from injected dependencies
   */
  _findCalendarInstance() {
    const sources = [
      this._calendarRenderer?.calendar,
      this._calendarRenderer?.getCalendar?.(),
      this._calendarApp?.state?.components?.get('calendar'),
      this._calendarApp?.getComponent?.('calendar')
    ];

    for (const calendar of sources) {
      if (calendar && calendar.getEvents && calendar.view?.type) {
        return calendar;
      }
    }

    return null;
  }

  /**
   * Update component references after construction (for late binding)
   * @param {Object} refs - Component references to update
   */
  setComponentRefs(refs = {}) {
    if (refs.calendarApp !== undefined) this._calendarApp = refs.calendarApp;
    if (refs.eventDataManager !== undefined) this._eventDataManager = refs.eventDataManager;
    if (refs.calendarRenderer !== undefined) this._calendarRenderer = refs.calendarRenderer;
    if (refs.modalAdapter !== undefined) this._modalAdapter = refs.modalAdapter;
  }

  /**
   * Get recent errors for debugging
   */
  getRecentErrors(minutes = 5) {
    return this.errorHandler.getRecentErrors(minutes);
  }

  /**
   * Check if there are critical issues
   */
  hasCriticalIssues() {
    return this.errorHandler.hasCriticalIssues();
  }

  /**
   * Clear error history
   */
  clearErrorHistory() {
    this.errorHandler.clearErrorHistory();
  }
}
